/**
 * Seamless looping marquee for homepage carousels.
 *
 * Motion uses the Web Animations API (compositor-friendly) with a measured
 * pixel distance at constant px/sec. That stays smooth in the website editor
 * even when React is busy — unlike a main-thread rAF loop, which hitchs next
 * to dashboard chrome. Falls back to rAF when `Element.animate` is missing.
 *
 * CSS `translateX(-50%)` is avoided (unreliable with `width: max-content` on iOS).
 *
 * Editor speed follows `data-tpl-device` / `data-template-device`, not the squeezed
 * preview column. Hover-pause is off in the editor; pointer drag still works.
 */

import React from "react";

/**
 * Below this unique-item count, prefer ticker (one copy). Loop mode duplicates
 * the track for a seamless wrap, which makes the same post/product appear
 * multiple times on screen when the strip is short (e.g. 2–3 custom products).
 */
export const MARQUEE_LOOP_MIN_ITEMS = 5;

/** True when a short strip should scroll as a single ticker, not a duplicated loop. */
export function shouldMarqueeTicker(itemCount: number): boolean {
  return itemCount > 0 && itemCount < MARQUEE_LOOP_MIN_ITEMS;
}

type Props = {
  children: React.ReactNode;
  /** When true, freeze marquee motion (mobile tap-to-focus). */
  paused?: boolean;
  /** Horizontal speed. Desktop default 60; slower on tablet/phone. */
  pxPerSec?: number;
  /** Extra class on the moving track (appended after defaults unless `trackClassName`). */
  className?: string;
  /** Override outer shell class (default: carousel marquee outer). */
  outerClassName?: string;
  /** Optional style on the outer shell (e.g. CSS vars for logo sizing). */
  outerStyle?: React.CSSProperties;
  /** Replace default track classes entirely (e.g. ribbon banners). */
  trackClassName?: string;
  /**
   * loop: caller duplicates children; wrap at half the track (carousels).
   * ticker: one copy enters from the right edge, exits left, then restarts.
   */
  mode?: "loop" | "ticker";
};

/** Pure default used when `pxPerSec` is omitted — desktop (≥1024) stays 60. */
export function defaultMarqueePxPerSec(widthPx: number): number {
  if (widthPx <= 480) return 36;
  if (widthPx <= 768) return 42;
  if (widthPx <= 1023) return 48;
  return 60;
}

/** Editor device preview → public-equivalent speed (ignore squeezed column width). */
export function marqueePxPerSecForDevice(
  device: string | null | undefined,
  widthPx: number,
): number {
  const d = String(device || "").toLowerCase();
  if (d === "mobile") return 36;
  if (d === "tablet") return 48;
  if (d === "desktop") return 60;
  return defaultMarqueePxPerSec(widthPx);
}

function previewDeviceFor(el: HTMLElement | null): string | null {
  if (!el) return null;
  const node =
    el.closest<HTMLElement>("[data-tpl-device]") ||
    el.closest<HTMLElement>("[data-template-device]");
  if (!node) return null;
  return (
    node.getAttribute("data-tpl-device") ||
    node.getAttribute("data-template-device")
  );
}

function wrapOffset(value: number, span: number): number {
  if (!(span > 1)) return 0;
  const mod = value % span;
  return mod < 0 ? mod + span : mod;
}

function previewRootFor(el: HTMLElement | null): Element | null {
  if (!el) return null;
  return (
    el.closest(".mte-root-scroll, .mte-wem-canvas, [data-template-scroll]") ||
    null
  );
}

function isEditorPreview(el: HTMLElement | null): boolean {
  return Boolean(el?.closest(".mte-root--editor"));
}

function supportsWaapi(el: HTMLElement): boolean {
  return typeof el.animate === "function";
}

export function TemplateMarqueeTrack({
  children,
  paused = false,
  pxPerSec,
  className,
  outerClassName = "mte-tpl-carl-marquee-outer",
  outerStyle,
  trackClassName,
  mode = "loop",
}: Props) {
  const outerRef = React.useRef<HTMLDivElement | null>(null);
  const trackRef = React.useRef<HTMLDivElement | null>(null);
  const offsetRef = React.useRef(0);
  const halfRef = React.useRef(0);
  /** Ticker: visible width of the clip, and width of one full right-to-left pass. */
  const viewportRef = React.useRef(0);
  const cycleRef = React.useRef(0);
  const animRef = React.useRef<Animation | null>(null);
  const durationMsRef = React.useRef(0);
  const spanRef = React.useRef(0);
  const rafRef = React.useRef(0);
  const useWaapiRef = React.useRef(true);
  /** Visible in viewport (IntersectionObserver). */
  const inViewRef = React.useRef(true);
  /** Desktop hover pause — never sticky on touch; skipped in editor. */
  const hoverPausedRef = React.useRef(false);
  const tapPausedRef = React.useRef(false);
  const dragPausedRef = React.useRef(false);
  const lastTsRef = React.useRef(0);
  const speedRef = React.useRef(60);
  const modeRef = React.useRef(mode);
  modeRef.current = mode;
  const syncPlayRef = React.useRef<() => void>(() => {});

  React.useEffect(() => {
    tapPausedRef.current = paused;
    syncPlayRef.current();
  }, [paused]);

  const isRunning = () =>
    inViewRef.current &&
    !hoverPausedRef.current &&
    !tapPausedRef.current &&
    !dragPausedRef.current;

  const applyTransform = React.useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    if (modeRef.current === "ticker") {
      const viewport = viewportRef.current;
      if (viewport > 0) {
        el.style.visibility = "visible";
        el.style.transform = `translate3d(${viewport - offsetRef.current}px, 0, 0)`;
      }
      return;
    }
    el.style.transform = `translate3d(${-offsetRef.current}px, 0, 0)`;
  }, []);

  const syncSpeedFromWidth = React.useCallback(() => {
    if (pxPerSec != null) {
      speedRef.current = pxPerSec;
      return;
    }
    const outer = outerRef.current;
    const width =
      outer?.clientWidth ||
      (typeof window !== "undefined" ? window.innerWidth : 1280);
    speedRef.current = marqueePxPerSecForDevice(
      previewDeviceFor(outer),
      width,
    );
  }, [pxPerSec]);

  React.useEffect(() => {
    syncSpeedFromWidth();
  }, [syncSpeedFromWidth]);

  React.useLayoutEffect(() => {
    const el = trackRef.current;
    const outer = outerRef.current;
    if (!el || typeof window === "undefined") return;

    offsetRef.current = 0;
    halfRef.current = 0;
    viewportRef.current = 0;
    cycleRef.current = 0;
    durationMsRef.current = 0;
    spanRef.current = 0;
    useWaapiRef.current = supportsWaapi(el);

    const reduceMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    )?.matches;
    if (reduceMotion) {
      el.style.transform = "";
      el.style.visibility = "";
      return;
    }

    const inEditor = isEditorPreview(outer);

    const cancelAnim = () => {
      try {
        animRef.current?.cancel();
      } catch {
        /* ignore */
      }
      animRef.current = null;
    };

    const readOffsetFromAnim = (): number => {
      const anim = animRef.current;
      const dur = durationMsRef.current;
      const span = spanRef.current;
      if (!anim || !(dur > 0) || !(span > 1)) return offsetRef.current;
      const t = Number(anim.currentTime) || 0;
      return ((t % dur) / dur) * span;
    };

    const keyframesFor = (span: number, viewport: number) => {
      if (mode === "ticker") {
        return [
          { transform: `translate3d(${viewport}px, 0, 0)` },
          { transform: `translate3d(${viewport - span}px, 0, 0)` },
        ];
      }
      return [
        { transform: "translate3d(0px, 0, 0)" },
        { transform: `translate3d(${-span}px, 0, 0)` },
      ];
    };

    const startWaapi = (progress01: number) => {
      if (!useWaapiRef.current) return false;
      const span =
        mode === "ticker" ? cycleRef.current : halfRef.current;
      const viewport = viewportRef.current;
      const speed = speedRef.current;
      if (!(span > 1) || !(speed > 0)) return false;
      if (mode === "ticker" && !(viewport > 0)) return false;

      const duration = Math.max(16, (span / speed) * 1000);
      const progress = Math.min(1, Math.max(0, progress01));

      cancelAnim();
      // Clear inline transform so WAAPI owns the compositor layer.
      el.style.transform = "";
      if (mode === "ticker") el.style.visibility = "visible";

      try {
        const anim = el.animate(keyframesFor(span, viewport), {
          duration,
          iterations: Infinity,
          easing: "linear",
          fill: "both",
        });
        anim.currentTime = progress * duration;
        animRef.current = anim;
        durationMsRef.current = duration;
        spanRef.current = span;
        offsetRef.current = progress * span;
        if (!isRunning()) anim.pause();
        else if (anim.playState === "paused") anim.play();
        return true;
      } catch {
        useWaapiRef.current = false;
        cancelAnim();
        return false;
      }
    };

    const syncPlayState = () => {
      if (dragPausedRef.current) return;
      if (useWaapiRef.current && animRef.current) {
        const anim = animRef.current;
        try {
          if (isRunning()) {
            if (anim.playState === "paused") anim.play();
          } else if (anim.playState === "running") {
            anim.pause();
          }
        } catch {
          /* ignore */
        }
        return;
      }
      // rAF path reads isRunning() each frame — nothing to sync here.
    };
    syncPlayRef.current = syncPlayState;

    const measureLoopHalf = (): number => {
      const kids = el.children;
      const copies =
        kids.length >= 2 && kids.length % 2 === 0 ? kids.length / 2 : 0;
      if (copies > 0) {
        const start = kids[0] as HTMLElement;
        const next = kids[copies] as HTMLElement;
        const loop = next.offsetLeft - start.offsetLeft;
        return loop > 1 ? loop : el.scrollWidth / 2;
      }
      return el.scrollWidth / 2;
    };

    const measure = () => {
      syncSpeedFromWidth();
      const prevSpan = spanRef.current;
      let progress =
        useWaapiRef.current && animRef.current
          ? (() => {
              const dur = durationMsRef.current;
              if (!(dur > 0)) return offsetRef.current / Math.max(prevSpan, 1);
              const t = Number(animRef.current!.currentTime) || 0;
              return (t % dur) / dur;
            })()
          : prevSpan > 1
            ? offsetRef.current / prevSpan
            : 0;

      if (mode === "ticker") {
        const viewport = outer?.clientWidth ?? 0;
        const content = el.scrollWidth;
        const nextCycle = viewport + content;
        viewportRef.current = viewport;
        cycleRef.current = nextCycle;
        const spanChanged = Math.abs(nextCycle - prevSpan) > 2;
        const speedDur = nextCycle > 1 ? (nextCycle / speedRef.current) * 1000 : 0;
        const durChanged = Math.abs(speedDur - durationMsRef.current) > 48;
        if (!(nextCycle > viewport && viewport > 0)) {
          cancelAnim();
          return;
        }
        if (!animRef.current || spanChanged || durChanged) {
          if (!(prevSpan > 1)) progress = 0;
          if (!startWaapi(progress)) {
            offsetRef.current = progress * nextCycle;
            applyTransform();
            ensureRafFallback();
          }
        }
        return;
      }

      const nextHalf = measureLoopHalf();
      halfRef.current = nextHalf;
      const spanChanged = Math.abs(nextHalf - prevSpan) > 2;
      const speedDur = nextHalf > 1 ? (nextHalf / speedRef.current) * 1000 : 0;
      const durChanged = Math.abs(speedDur - durationMsRef.current) > 48;
      if (!(nextHalf > 1)) {
        cancelAnim();
        return;
      }
      if (!animRef.current || spanChanged || durChanged) {
        if (!(prevSpan > 1)) progress = 0;
        if (!startWaapi(progress)) {
          offsetRef.current = progress * nextHalf;
          applyTransform();
          ensureRafFallback();
        }
      }
    };

    let measureRaf = 0;
    const scheduleMeasure = () => {
      if (measureRaf) return;
      measureRaf = requestAnimationFrame(() => {
        measureRaf = 0;
        measure();
      });
    };

    // rAF fallback when WAAPI is unavailable.
    const tick = (now: number) => {
      if (useWaapiRef.current && animRef.current) {
        rafRef.current = 0;
        return;
      }
      if (!isRunning()) {
        lastTsRef.current = now;
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      const last = lastTsRef.current || now;
      const dt = Math.min(0.032, (now - last) / 1000);
      lastTsRef.current = now;
      if (mode === "ticker") {
        const cycle = cycleRef.current;
        const viewport = viewportRef.current;
        if (cycle > viewport && viewport > 0) {
          offsetRef.current = (offsetRef.current + speedRef.current * dt) % cycle;
          el.style.transform = `translate3d(${viewport - offsetRef.current}px, 0, 0)`;
        }
      } else {
        const half = halfRef.current;
        if (half > 1) {
          offsetRef.current = (offsetRef.current + speedRef.current * dt) % half;
          el.style.transform = `translate3d(${-offsetRef.current}px, 0, 0)`;
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    const ensureRafFallback = () => {
      if (useWaapiRef.current && animRef.current) return;
      if (rafRef.current) return;
      lastTsRef.current = 0;
      rafRef.current = requestAnimationFrame(tick);
    };

    measure();
    const remMeasure = window.setTimeout(measure, 120);
    const remMeasure2 = window.setTimeout(measure, 600);

    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            scheduleMeasure();
          })
        : null;
    ro?.observe(el);
    if (outer && outer !== el) ro?.observe(outer);

    inViewRef.current = true;
    hoverPausedRef.current = false;
    dragPausedRef.current = false;
    lastTsRef.current = 0;
    if (!useWaapiRef.current) ensureRafFallback();

    const ioRoot = previewRootFor(outer);
    const io =
      typeof IntersectionObserver !== "undefined"
        ? new IntersectionObserver(
            (entries) => {
              const hit = entries[0];
              inViewRef.current = Boolean(hit?.isIntersecting);
              syncPlayState();
            },
            { root: ioRoot, threshold: 0, rootMargin: "40px 0px" },
          )
        : null;
    if (outer && io) io.observe(outer);

    const canHoverPause =
      !inEditor &&
      window.matchMedia?.("(hover: hover) and (pointer: fine)")?.matches;
    const onEnter = () => {
      hoverPausedRef.current = true;
      syncPlayState();
    };
    const onLeave = () => {
      hoverPausedRef.current = false;
      syncPlayState();
    };
    if (outer && canHoverPause) {
      outer.addEventListener("pointerenter", onEnter);
      outer.addEventListener("pointerleave", onLeave);
    }

    let dragPointerId: number | null = null;
    let dragStartX = 0;
    let dragStartOffset = 0;
    let dragged = false;

    const spanForMode = () =>
      mode === "ticker" ? cycleRef.current : halfRef.current;

    const onPointerDown = (event: PointerEvent) => {
      if (event.button != null && event.button !== 0) return;
      if (!outer) return;
      dragPointerId = event.pointerId;
      dragStartX = event.clientX;
      offsetRef.current = readOffsetFromAnim();
      dragStartOffset = offsetRef.current;
      dragged = false;
      dragPausedRef.current = true;
      // Commit current frame to an inline transform, then scrub manually.
      cancelAnim();
      applyTransform();
      try {
        outer.setPointerCapture(event.pointerId);
      } catch {
        /* ignore */
      }
    };

    const onPointerMove = (event: PointerEvent) => {
      if (dragPointerId !== event.pointerId) return;
      const dx = event.clientX - dragStartX;
      if (!dragged && Math.abs(dx) < 4) return;
      dragged = true;
      event.preventDefault();
      const span = spanForMode();
      offsetRef.current = wrapOffset(dragStartOffset - dx, span);
      applyTransform();
    };

    const endDrag = (event: PointerEvent) => {
      if (dragPointerId !== event.pointerId) return;
      dragPointerId = null;
      dragPausedRef.current = false;
      const span = spanForMode();
      const progress = span > 1 ? offsetRef.current / span : 0;
      if (!startWaapi(progress)) {
        ensureRafFallback();
        syncPlayState();
      }
      try {
        outer?.releasePointerCapture(event.pointerId);
      } catch {
        /* ignore */
      }
    };

    const onClickCapture = (event: MouseEvent) => {
      if (!dragged) return;
      event.preventDefault();
      event.stopPropagation();
      dragged = false;
    };

    if (outer) {
      outer.addEventListener("pointerdown", onPointerDown);
      outer.addEventListener("pointermove", onPointerMove);
      outer.addEventListener("pointerup", endDrag);
      outer.addEventListener("pointercancel", endDrag);
      outer.addEventListener("click", onClickCapture, true);
    }

    return () => {
      cancelAnimationFrame(rafRef.current);
      if (measureRaf) cancelAnimationFrame(measureRaf);
      window.clearTimeout(remMeasure);
      window.clearTimeout(remMeasure2);
      cancelAnim();
      ro?.disconnect();
      io?.disconnect();
      syncPlayRef.current = () => {};
      if (outer && canHoverPause) {
        outer.removeEventListener("pointerenter", onEnter);
        outer.removeEventListener("pointerleave", onLeave);
      }
      if (outer) {
        outer.removeEventListener("pointerdown", onPointerDown);
        outer.removeEventListener("pointermove", onPointerMove);
        outer.removeEventListener("pointerup", endDrag);
        outer.removeEventListener("pointercancel", endDrag);
        outer.removeEventListener("click", onClickCapture, true);
      }
      el.style.transform = "";
      el.style.visibility = "";
    };
  }, [mode, syncSpeedFromWidth, applyTransform]);

  return (
    <div
      ref={outerRef}
      className={outerClassName}
      style={{ touchAction: "pan-y", ...outerStyle }}
      data-mte-marquee="true"
    >
      <div
        ref={trackRef}
        style={mode === "ticker" ? { visibility: "hidden" } : undefined}
        className={[
          trackClassName ||
            "mte-tpl-carl-marquee-track mte-tpl-carl-marquee-track--js",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {children}
      </div>
    </div>
  );
}
